import { KitRequestStatus } from 'types/kitting';
import { AllocationSchema } from 'types/clientV2/allocations';
import { KitRequestSubtype } from 'views/kitting/Kit/KitRequestContext';
import {
  KitRequestAllocationStatus,
  CalculateKitRequestStatusReturn,
  getLinePrimaryStatus,
} from 'hooks/useGetDecoratedKitRequestLines/utils';
import { COLOR_OPTIONS } from 'types';
import { ProgressStackProps } from 'ui-component/ProgressStack';

export const calculateKitRequestStatus = (kitRequest: KitRequestSubtype) => {
  if (kitRequest.voidedAt) {
    return KitRequestStatus.VOIDED;
  }
  if (kitRequest.fulfilledAt) {
    return KitRequestStatus.FULFILLED;
  }
  if (kitRequest.approved) {
    return KitRequestStatus.APPROVED;
  }
  return KitRequestStatus.DRAFT;
};

export const calculateAvailableQuantForStockLot = ({
  allocations,
  stockLotQuant,
  kitRequest,
  kitRequestLineId,
}: {
  allocations: AllocationSchema[];
  stockLotQuant: number;
  kitRequest: KitRequestSubtype | null;
  kitRequestLineId?: KitRequestSubtype['id'];
}) => {
  const totalAllocations = allocations.reduce((total, allocation) => {
    if (
      ((!allocation.productionRun && !kitRequest?.productionRun?.id) ||
        allocation.productionRun?.id !== kitRequest?.productionRun?.id) &&
      (!kitRequestLineId ||
        !allocation.kitRequestLine ||
        allocation.kitRequestLine.id !== kitRequestLineId)
    ) {
      return total + allocation.quant;
    }
    return total;
  }, 0);

  return stockLotQuant - totalAllocations;
};

const statusColorMap: Record<KitRequestAllocationStatus, COLOR_OPTIONS> = {
  [KitRequestAllocationStatus.Unavailable]: COLOR_OPTIONS.error,
  [KitRequestAllocationStatus.Expected]: COLOR_OPTIONS.secondary,
  [KitRequestAllocationStatus.OnHand]: COLOR_OPTIONS.primary,
  [KitRequestAllocationStatus.Quarantined]: COLOR_OPTIONS.warning,
  [KitRequestAllocationStatus.ReadyToPack]: COLOR_OPTIONS.warning,
  [KitRequestAllocationStatus.Packed]: COLOR_OPTIONS.success,
  [KitRequestAllocationStatus.Shipped]: COLOR_OPTIONS.success,
  [KitRequestAllocationStatus.Delivered]: COLOR_OPTIONS.success,
  [KitRequestAllocationStatus.NotInFacility]: 'grey' as COLOR_OPTIONS,
  [KitRequestAllocationStatus.Unknown]: 'grey' as COLOR_OPTIONS,
};

const statusLabelMap: Record<KitRequestAllocationStatus, string> = {
  [KitRequestAllocationStatus.Unavailable]: 'Unavailable',
  [KitRequestAllocationStatus.Expected]: 'Expected',
  [KitRequestAllocationStatus.OnHand]: 'On Hand',
  [KitRequestAllocationStatus.Quarantined]: 'Quarantined',
  [KitRequestAllocationStatus.ReadyToPack]: 'Ready to Pack',
  [KitRequestAllocationStatus.Packed]: 'Packed',
  [KitRequestAllocationStatus.Shipped]: 'Shipped',
  [KitRequestAllocationStatus.Delivered]: 'Delivered',
  [KitRequestAllocationStatus.NotInFacility]: 'Not In Facility',
  [KitRequestAllocationStatus.Unknown]: 'Unknown',
};

export const getProgressStackData = (
  kitRequestStatuses: CalculateKitRequestStatusReturn | undefined
): ProgressStackProps['bars'] => {
  if (!kitRequestStatuses) return [];

  const { lineStats } = kitRequestStatuses;

  const lineStatusCounts: Record<KitRequestAllocationStatus, number> = {
    [KitRequestAllocationStatus.Unavailable]: 0,
    [KitRequestAllocationStatus.Expected]: 0,
    [KitRequestAllocationStatus.OnHand]: 0,
    [KitRequestAllocationStatus.Quarantined]: 0,
    [KitRequestAllocationStatus.ReadyToPack]: 0,
    [KitRequestAllocationStatus.Packed]: 0,
    [KitRequestAllocationStatus.Shipped]: 0,
    [KitRequestAllocationStatus.Delivered]: 0,
    [KitRequestAllocationStatus.NotInFacility]: 0,
    [KitRequestAllocationStatus.Unknown]: 0,
  };

  Object.values(lineStats).forEach((line) => {
    const primaryStatus = getLinePrimaryStatus(line);
    lineStatusCounts[primaryStatus] += 1;
  });

  return [
    {
      key: 'kit-request-status',
      label: 'Kit Status',
      sections: Object.entries(lineStatusCounts)
        .filter(([_, count]) => count > 0)
        .map(([status, value]) => ({
          key: status,
          label: statusLabelMap[status as KitRequestAllocationStatus],
          value,
          color: statusColorMap[status as KitRequestAllocationStatus],
        })),
    },
  ];
};
